import { AxiosError } from "axios";

import { http } from "@/shared/api/http";
import { getStoredAuthToken, type AuthSession } from "@/shared/auth/session";
import type { AppLanguage } from "@/shared/config/i18n/config";

export type LoginV2Payload = {
  phone: string;
  password: string;
};

export type StartOtpPayload = {
  phone: string;
};

export type StartRegisterPayload = {
  first_name: string;
  last_name: string;
  phone: string;
  password: string;
  language: AppLanguage;
};

export type VerifyOtpPayload = {
  request_id: string;
  code: string;
};

export type StartPasswordResetPayload = {
  phone: string;
};

export type CompletePasswordResetPayload = {
  reset_token: string;
  new_password: string;
};

export type PasswordResetVerifyResult = {
  reset_token: string;
  expires_in: number;
};

export type CompletePasswordChangePayload = {
  current_password: string;
  new_password: string;
};

export type OtpStartResult = {
  request_id: string;
  code_length: number;
  expires_in: number;
};

// Backend returns the auth token in the response body as well as setting it in
// an HttpOnly cookie. The body copy lets clients fall back to
// `Authorization: Bearer` when third-party cookies are blocked (iOS, etc).
type AuthSessionResponse = { data: AuthSession & { token?: string } };
type OtpStartResponse = { data: OtpStartResult };

export type AuthErrorBody = {
  error?: string;
  status?: string;
  code?: string;
};

export type AuthError = {
  status: number | null;
  body: AuthErrorBody;
  isNetworkError: boolean;
  cause: unknown;
};

export function toAuthError(error: unknown): AuthError {
  if (error instanceof AxiosError) {
    if (!error.response) {
      return { status: null, body: {}, isNetworkError: true, cause: error };
    }
    const body = (error.response.data ?? {}) as AuthErrorBody;
    return { status: error.response.status, body, isNetworkError: false, cause: error };
  }

  return { status: null, body: {}, isNetworkError: false, cause: error };
}

export async function loginV2(payload: LoginV2Payload) {
  const { data } = await http.post<AuthSessionResponse>("/app/auth/login", payload);
  return data.data;
}

export async function fetchCurrentUser() {
  const { data } = await http.get<{ data: AuthSession["user"] }>("/app/auth/me");
  return data.data;
}

export async function startLoginOtp(payload: StartOtpPayload) {
  const { data } = await http.post<OtpStartResponse>("/app/auth/login/otp/start", payload);
  return data.data;
}

export async function verifyLoginOtp(payload: VerifyOtpPayload) {
  const { data } = await http.post<AuthSessionResponse>("/app/auth/login/otp/verify", payload);
  return data.data;
}

export async function startRegister(payload: StartRegisterPayload) {
  const { data } = await http.post<OtpStartResponse>("/app/auth/register/start", payload);
  return data.data;
}

export async function verifyRegister(payload: VerifyOtpPayload) {
  const { data } = await http.post<AuthSessionResponse>("/app/auth/register/verify", payload);
  return data.data;
}

export async function startPasswordReset(payload: StartPasswordResetPayload) {
  const { data } = await http.post<OtpStartResponse>("/app/auth/forgot-password/start", payload);
  return data.data;
}

export async function verifyPasswordReset(payload: VerifyOtpPayload): Promise<PasswordResetVerifyResult> {
  const { data } = await http.post<{ data: PasswordResetVerifyResult }>("/app/auth/forgot-password/verify", payload);
  return data.data;
}

export async function completePasswordReset(payload: CompletePasswordResetPayload): Promise<void> {
  await http.post("/app/auth/forgot-password/complete", payload);
}

export async function completePasswordChange(payload: CompletePasswordChangePayload): Promise<void> {
  await http.post("/app/account/password/change", payload);
}

export async function logoutRequest() {
  // Read the bearer token synchronously and attach it ourselves: the caller
  // clears local storage right after this returns, which would race the
  // request interceptor (it awaits fingerprint before reading the token).
  const token = typeof window === "undefined" ? null : getStoredAuthToken();
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  await http.post("/app/auth/logout", undefined, headers ? { headers } : undefined);
}
